import { attendanceApi, type StudentAttendanceGroup } from '@mathflat/domain/Attendance'
import {
  lessonClassApi,
  type LessonClassAttendanceGroup,
  type LessonClassAttendanceGroupParams,
} from '@mathflat/domain/LessonClass'
import {
  type Student as StudentData,
  studentApi,
  type StudentAppSetting as StudentAppSettingData,
  type StudentAppSettingUpdateBody,
  type StudentCreateRequest,
  type StudentDeleteRequest,
  type StudentExcelParams,
  type StudentListParams,
  type StudentSheetParams,
  type StudentStatus,
  type StudentStatusUpdateRequest,
  type StudentSummary,
  type StudentUpdateRequest,
} from '@mathflat/domain/Student'
import { flow } from 'lodash-es'
import { partial } from 'lodash-es'
import { sortBy } from 'lodash-es'
import { makeObservable, observable, runInAction } from 'mobx'

import { MAX_REQUEST_NUMBER, 가상학생목록 } from '@/constant/common'
import {
  Student,
  StudentAppSettingColl,
  StudentColl,
  StudentDetail,
  StudentGroupWithAttendance,
  StudentGroupWithAttendanceColl,
} from '@/dto'
import { ReqParams } from '@/dto/common/ReqParams'
import { LessonClassWithStudentId } from '@/dto/LessonClass/LessonClassWithStudentId'
import { StudentGroups } from '@/dto/Student/StudentGroups'
import ExtendedMap from '@/models/ExtendedMap'
import commonRepo from '@/repository/common.repo'
import { emptyStringToNull } from '@/utils/common'

import { makeAdapter } from './Adapter'
import type Controller from './Controller'
import queryClient from './queryClient'

export type StudentFilterOptions = Omit<StudentListParams, 'page' | 'size' | 'sort'>

type CacheData = {
  studentAttendanceColl?: StudentGroupWithAttendanceColl
  studentColl?: StudentColl
}

/***
 * @see Student
 ***/
class StudentController implements Controller {
  adapter = {
    // TODO: 나중에 studentId 주는 API 추가되거나 변경되면 바꿔야함
    lessonClassWithStudentId: makeAdapter({
      from(data: LessonClassAttendanceGroup) {
        return new LessonClassWithStudentId(data)
      },
    }),
    student: makeAdapter({
      from(data: StudentSummary): Student {
        if (data.name in 가상학생목록) {
          if (commonRepo.academy && !commonRepo.academy.가상학생등록여부) {
            commonRepo.academy?.update({ 가상학생등록여부: true })
          }
          return new Student({ ...data, isVirtual: true })
        }
        return new Student(data)
      },
    }),
    studentDetail: makeAdapter({
      from(data: StudentData) {
        if (data.name in 가상학생목록) {
          return new StudentDetail({ ...data, isVirtual: true })
        }
        return new StudentDetail(data)
      },
      toCreate(data: StudentDetail): StudentCreateRequest {
        return {
          name: data.name,
          schoolType: data.schoolGrade.schoolType,
          grade: data.schoolGrade.grade,
          schoolName: data.schoolName,
          // 폼은 0 을 채운 네 자리 문자열로 들고 있고 서버는 정수(0~9999)로 받는다. 빈 값은 NaN 이 되어
          // JSON 에서 null 로 나가므로 서버가 거부한다.
          attendanceCode: Number.parseInt(data.attendanceCode, 10),
          email: data.email,
          address: data.address,
          phone: data.phone,
          parentPhone: data.parent.phone,
          homePhone: data.homePhone,
          memo: data.memo,
          startDate: data.startDate,
          birthDate: data.birthDate,
          status: commonRepo.isSchoolflat ? 'INACTIVE' : 'ACTIVE',
        }
      },
      toChange(data: StudentDetail): StudentUpdateRequest {
        return {
          ...this.toCreate(data),
          status: data.status,
          allowedServiceTypes: data.allowedServiceTypes,
        }
      },
    }),
    // 학생 앱 정답 공개 설정
    studentAppSettings: makeAdapter({
      from({
        studentColl,
        appSettings,
      }: {
        studentColl: StudentColl
        appSettings: StudentAppSettingData[]
      }) {
        return new StudentAppSettingColl(studentColl, appSettings)
      },
      to(studentAppSettings: StudentAppSettingColl): StudentAppSettingUpdateBody[] {
        return Array.from(studentAppSettings.settings, (setting) => ({
          studentId: setting.studentId, //TODO: studentId로 통일 예정
          showSolutionBeforeScoring: setting.채점전_정답공개,
          showSolutionAfterScoring: setting.채점후_정답해설공개,
          showProblemVideoBeforeScoring: setting.채점전_문풀동공개,
          showProblemVideoAfterScoring: setting.채점후_문풀동공개,
          showSolutionToParent: setting.학부모_공개,
          useAiForHintConceptual: setting.개념힌트_사용,
          useAiForSolveTheStuckUp: setting.풀이분석_사용,
          useAiForReviewTheSolution: setting.풀이확인_사용,
        }))
      },
    }),
    gradeAttendance: makeAdapter({
      from(data: StudentAttendanceGroup) {
        // FIXME: 밑에 2개 api에서 정렬해주면 정렬로직 삭제
        return new StudentGroupWithAttendance({
          grade: data.grade,
          schoolType: data.schoolType,
          memberList: sortBy(data.memberList, (member) => member.name),
        })
      },
    }),
    // deprecated
    lessonClassesAttendance: makeAdapter({
      from(data: LessonClassAttendanceGroup) {
        return new StudentGroupWithAttendance({
          id: data.id,
          name: data.name,
          memberList: sortBy(data.memberList, ({ name }) => name),
        })
      },
    }),
  }
  cacheData = {
    studentGroupWithAttendanceColl: undefined,
    studentColl: undefined,
  } as CacheData
  fetchMaximumFilterStudents = flow(
    partial(
      this.fetchStudents,
      new ReqParams({ params: { size: MAX_REQUEST_NUMBER, sorts: [['name', 'ASC']] } })
        .toRequestParams,
    ),
    async (promise) => {
      const { studentColl } = await promise
      return studentColl
    },
  )
  private studentGroups: StudentGroups | null = null

  constructor() {
    makeObservable<this, 'studentGroups'>(this, {
      studentGroups: observable,
    })
  }

  // 모듈 로드 시점에 new StudentGroups() 를 실행하면 순환 import 로 StudentGroups 가 아직
  // 초기화되지 않아 'is not a constructor' 로 크래시할 수 있어, 최초 사용 시점에 지연 생성한다.
  private ensureStudentGroups(): StudentGroups {
    const existing = this.studentGroups
    if (existing) return existing
    const created = new StudentGroups()
    runInAction(() => {
      this.studentGroups = created
    })
    return created
  }

  async fetchHasStudent() {
    const { content } = await studentApi.getStudents(
      { page: 0, size: 1, status: ['ACTIVE', 'INACTIVE'] },
      { mp_event_name: 'T51-01_학생조회_API' },
    )

    return content.length > 0
  }
  // 활성 학생 수 가져오기
  async fetchActiveStudents() {
    const { totalElements } = await studentApi.getStudents(
      { page: 0, size: 1, status: ['ACTIVE'] },
      { mp_event_name: 'T51-01_학생조회_API' },
    )

    return totalElements
  }
  async fetchAttendanceCode() {
    return studentApi.getUsableAttendanceCode({ mp_event_name: 'T51-04_학생출결번호재발급_API' })
  }

  async fetchStudents(pageParams: StudentListParams, filterOptions?: StudentFilterOptions) {
    const { content, last, totalPages, totalElements } = await studentApi.getStudents(
      { ...pageParams, ...(filterOptions && emptyStringToNull(filterOptions)) },
      { mp_event_name: 'T51-01_학생조회_API' },
    )
    const studentsData = content.map(this.adapter.student.from)

    return { studentColl: new StudentColl(studentsData), last, totalPages, totalElements }
  }

  async fetchMaximumStudents({
    studentStatus = ['ACTIVE'],
    ignoreHomeroom = false,
  }: {
    studentStatus?: StudentStatus[]
    /** true 면 GET /students 에 bypassRoleScope=true 를 보내 홈룸 권한을 무시하고 학원 전체 학생을 조회 */
    ignoreHomeroom?: boolean
  }) {
    const reqParams = new ReqParams({
      params: {
        size: MAX_REQUEST_NUMBER,
        sorts: [['name', 'ASC']],
      },
    })

    const fetchData = await queryClient.fetchQuery({
      // ignoreHomeroom 을 키에 포함해 전체조회/제한조회 캐시를 분리한다.
      // 키 구성요소가 바뀌면 이 키를 exact-match(getQueryData)로 읽는 코드와 어긋나므로 주의.
      // reqParams 는 모듈 상수와 리터럴로만 만들어져 이 함수 안에서 불변이라 키에 더할 값이 없다.
      // eslint-disable-next-line @tanstack/query/exhaustive-deps
      queryKey: ['fetchMaximumStudents', studentStatus, ignoreHomeroom],
      queryFn: () =>
        this.fetchStudents(reqParams.toRequestParams, {
          status: studentStatus,
          ...(ignoreHomeroom && { bypassRoleScope: true }),
        }),
    })

    const studentGroups = this.ensureStudentGroups()
    if (studentGroups.studentColl !== fetchData.studentColl) {
      studentGroups.setStudentColl(fetchData.studentColl)
    }

    if (!fetchData.studentColl) throw Error('데이터가 없음!')

    // 공유 필드가 아닌 이번 호출의 결과를 반환해 scope 혼선을 막는다.
    return fetchData.studentColl
  }

  async createVirtualStudents(newStudentInfos: StudentDetail[]) {
    const promises = newStudentInfos.map((student) => {
      return studentApi.createStudent(this.adapter.studentDetail.toCreate(student), {
        mp_event_name: 'T71-00_가상학생등록_API',
      })
    })
    return await Promise.all(promises).then(() => {
      queryClient.resetQueries({ queryKey: ['fetchStudentGroupAttendance'] })
      queryClient.resetQueries({ queryKey: ['fetchMaximumStudents'] })
    })
  }

  async createStudent(newStudentInfo: StudentDetail) {
    await studentApi.createStudent(this.adapter.studentDetail.toCreate(newStudentInfo), {
      mp_event_name: 'T51-03_학생등록_API',
    })
    queryClient.resetQueries({ queryKey: ['fetchStudentGroupAttendance'] })
    queryClient.resetQueries({ queryKey: ['fetchMaximumStudents'] })
  }

  async getStudentsXLS() {
    return studentApi.getStudentImportTemplate({
      mp_event_name: 'T51-02_학생일괄등록양식다운로드_API',
    })
  }

  async createStudentsXLS(params: StudentExcelParams, xls: FormData) {
    return studentApi.createStudentsByExcel(xls, params, {
      mp_event_name: 'T51-02_학생일괄등록_API',
    })
  }

  async fetchStudentDetailById(id: StudentData['id']) {
    const student = await studentApi.getStudent(id, { mp_event_name: 'T51-04_특정학생조회_API' })
    return this.adapter.studentDetail.from(student)
  }

  async fetchStudentsById(ids: StudentData['id'][]) {
    const data = await studentApi.getStudentsByIds(ids, {
      mp_event_name: 'T99-00_여러학생조회_API',
    })

    const studentsData = data.map(this.adapter.student.from)

    return new StudentColl(studentsData)
  }

  async changeStudentById(studentDetail: StudentDetail) {
    await studentApi.updateStudent(
      studentDetail.id,
      this.adapter.studentDetail.toChange(studentDetail),
      {
        mp_event_name: 'T51-04_특정학생수정_API',
      },
    )
    queryClient.resetQueries({ queryKey: ['fetchStudentGroupAttendance'] })
    queryClient.resetQueries({ queryKey: ['fetchMaximumStudents'] })
  }

  async setStatusActive(data: StudentStatusUpdateRequest) {
    await studentApi.updateStudentStatus(data, { mp_event_name: 'T51-01_여러학생수정_API' })
    queryClient.resetQueries({ queryKey: ['fetchStudentGroupAttendance'] })
    queryClient.resetQueries({ queryKey: ['fetchMaximumStudents'] })
  }

  async setStatusInactive(data: StudentStatusUpdateRequest) {
    await studentApi.updateStudentStatus(data, { mp_event_name: 'T51-01_여러학생수정_API' })
    queryClient.resetQueries({ queryKey: ['fetchStudentGroupAttendance'] })
    queryClient.resetQueries({ queryKey: ['fetchMaximumStudents'] })
  }

  async deleteStudents(data: StudentDeleteRequest, isDeleteAll?: boolean) {
    if (isDeleteAll) {
      await studentApi.deleteAllStudents(data, { mp_event_name: 'T51-01_학생전체삭제_API' })
    } else {
      await studentApi.deleteStudents(data, { mp_event_name: 'T51-01_여러학생삭제_API' })
    }
    queryClient.resetQueries({ queryKey: ['fetchStudentGroupAttendance'] })
    queryClient.resetQueries({ queryKey: ['fetchMaximumStudents'] })
  }

  async getAllStudentsListXLS(data: StudentFilterOptions & { studentIds?: StudentData['id'][] }) {
    const pageParms = new ReqParams({
      params: {
        status: data.status,
        grade: data.grade,
        schoolType: data.schoolType,
        studentIds: data.studentIds,
        name: data.name,
      },
    })
    return studentApi.getStudentSheet(pageParms.toRequestParams as StudentSheetParams)
  }

  /* 학생앱 설정 */
  async fetchAppSettings() {
    return studentApi.getStudentAppSettings({ mp_event_name: 'T51-16_학생앱설정조회_API' })
  }

  async changeStudentAppSettings(
    studentAppSettings: StudentAppSettingColl | StudentAppSettingUpdateBody[],
  ) {
    const data = Array.isArray(studentAppSettings)
      ? studentAppSettings
      : this.adapter.studentAppSettings.to(studentAppSettings)
    await studentApi.updateStudentAppSettings(data, { mp_event_name: 'T51-16_학생앱설정수정_API' })
  }

  // 학생 앱 정답 공개 설정
  async fetchStudentAppSettings() {
    const [studentColl, appSettings] = await Promise.all([
      studentController.fetchMaximumFilterStudents({ status: ['ACTIVE'] }),
      this.fetchAppSettings(),
    ])

    const studentAppSettings = this.adapter.studentAppSettings.from({
      studentColl,
      appSettings,
    })

    return studentAppSettings
  }

  // 아래 fetchStudentGroupAttendance을 대체함
  async fetchStudentGroups({
    studentStatus = ['ACTIVE'],
    ignoreHomeroom = false,
  }: {
    studentStatus?: StudentStatus[]
    /** 홈룸(담당 반) 권한을 무시하고 학원 전체 학생·반을 조회 */
    ignoreHomeroom?: boolean
  } = {}) {
    // scope(ignoreHomeroom)별 결과가 공유 필드에서 섞이지 않도록, 두 조회 결과로
    // 매 호출 새 StudentGroups 를 조립해 반환한다.
    const [studentColl, lessonClassColl] = await Promise.all([
      this.fetchMaximumStudents({ studentStatus, ignoreHomeroom }),
      this.fetchLessonClassWithStudentColl({ studentStatus, ignoreHomeroom }),
    ])
    const studentGroups = new StudentGroups()
    studentGroups.setStudentColl(studentColl)
    studentGroups.setLessonClassColl(lessonClassColl)
    return studentGroups
  }

  async fetchStudentGroupsByStudentIdList(studentIdList: Student['id'][]) {
    // 내부적으로 side effect 적으로 this.studentGroups의 멤버들을 세팅함...
    // 성능적으로 손해를 보지 않으면서 어떻게 더 좋게 할 수 있을까요?

    const lessonClassParams = new ReqParams({
      params: {
        page: 0,
        size: MAX_REQUEST_NUMBER,
        sorts: [['index', 'ASC']],
      },
    })
    return Promise.all([
      this.fetchStudentsById(studentIdList),
      queryClient.fetchQuery({
        // lessonClassParams 는 모듈 상수와 리터럴로만 만들어져 호출마다 같은 값이다.
        // eslint-disable-next-line @tanstack/query/exhaustive-deps
        queryKey: ['fetchLessonClassWithStudentColl'],
        queryFn: async () => {
          const { content } = await this.fetchLessonClassAttendanceGroups(
            lessonClassParams.toRequestParams,
          )
          const lessonClassesWithStudentId = content.map(this.adapter.lessonClassWithStudentId.from)
          return ExtendedMap.fromArr(lessonClassesWithStudentId, 'id')
        },
      }),
    ]).then(([studentColl, lessonClassColl]) => {
      const studentGroups = new StudentGroups()
      studentGroups.setStudentColl(studentColl)
      studentGroups.setLessonClassColl(lessonClassColl)
      return studentGroups
    })
  }

  private fetchLessonClassAttendanceGroups = (params: LessonClassAttendanceGroupParams) =>
    lessonClassApi.getLessonClassAttendanceGroups(params, {
      mp_event_name: 'T53-04_출결관리_반별보기_특정학생출결정보조회_API',
    })

  /* studentGroup */
  // 수업 - 학년, 반 학생리스트 정보 가져오기
  // TODO: 나중에 없애(제발) 버려야함(studentIdList를 주게 변경되거나 api가 생기면)
  // !deprecated
  fetchStudentGroupAttendance = async () => {
    const promise = Promise.all([
      this.fetchLessonClassesIncludeAttendance(),
      this.fetchGradeIncludeAttendance(),
    ])

    const cacheData = queryClient.getQueryData<
      [StudentGroupWithAttendance[], StudentGroupWithAttendance[]]
    >(['fetchStudentGroupAttendance'])
    const fetchData = await queryClient.fetchQuery({
      queryKey: ['fetchStudentGroupAttendance'],
      queryFn: () => promise,
    })

    if (cacheData !== fetchData) {
      this.cacheData.studentAttendanceColl = new StudentGroupWithAttendanceColl({
        studentByLessonClassArr: fetchData[0],
        studentByGradeArr: fetchData[1],
      })
    }

    if (!this.cacheData.studentAttendanceColl) throw Error('캐시된 데이터가 없음')

    return this.cacheData.studentAttendanceColl
  }

  // 도메인 기반이 아니라서 둘데가 없어서 여기둬요......
  // TODO: 나중에 변경되어야함(studentIdList를 주게 변경되거나 api가 생기면)
  async fetchLessonClassWithStudentColl({
    studentStatus = ['ACTIVE'],
    ignoreHomeroom = false,
  }: {
    studentStatus?: StudentStatus[]
    /** true 면 GET /lesson-classes 에 bypassRoleScope=true 를 보내 홈룸 권한을 무시하고 학원 전체 반을 조회 */
    ignoreHomeroom?: boolean
  }) {
    const params = new ReqParams({
      params: {
        page: 0,
        studentStatus,
        size: MAX_REQUEST_NUMBER,
        sorts: [['index', 'ASC']],
        ...(ignoreHomeroom && { bypassRoleScope: true }),
      },
    })
    // queryClient에 저장된 cachedData가 queryClient로 새로 불러온 fetchData와 reference가 같다면,
    // 전역 데이터인 기존 this.cacheData를 리턴하고(새로운 데이터로 전역 데이터를 교체하는 비용을 들이지 않음),
    // 다르다면 this.cachedData.lessonClassColl에 fetch해온 것을 넣어 전역 데이터를 만든다.
    // 목적 : 새로운 데이터로 전역데이터를 교체하는 비용을 들이지 않는게 포인트
    const lessonClassWithStudentColl = await queryClient.fetchQuery({
      // ignoreHomeroom 을 키에 포함해 전체조회/제한조회 캐시를 분리한다.
      // params 가 갈리는 축은 studentStatus 와 ignoreHomeroom 뿐이고 둘 다 이미 키에 있다.
      // eslint-disable-next-line @tanstack/query/exhaustive-deps
      queryKey: ['fetchLessonClassWithStudentColl', studentStatus, ignoreHomeroom],
      queryFn: async () => {
        const { content } = await this.fetchLessonClassAttendanceGroups(params.toRequestParams)
        const lessonClassesWithStudentId = content.map(this.adapter.lessonClassWithStudentId.from)
        return ExtendedMap.fromArr(lessonClassesWithStudentId, 'id')
      },
    })

    const studentGroups = this.ensureStudentGroups()
    if (studentGroups.lessonClassColl !== lessonClassWithStudentColl) {
      studentGroups.setLessonClassColl(lessonClassWithStudentColl)
    }

    if (!lessonClassWithStudentColl) throw Error('데이터가 없음!')

    // 공유 필드가 아닌 이번 호출의 결과를 반환해 scope 혼선을 막는다.
    return lessonClassWithStudentColl
  }

  // deprecated
  private fetchLessonClassesIncludeAttendance = async () => {
    const params = new ReqParams({
      params: {
        page: 0,
        size: MAX_REQUEST_NUMBER,
        sorts: [['index', 'ASC']],
      },
    })
    const { content } = await this.fetchLessonClassAttendanceGroups(params.toRequestParams)

    const attendanceArr = content.map(this.adapter.lessonClassesAttendance.from)

    return attendanceArr
  }

  // deprecated

  private fetchGradeIncludeAttendance = async () => {
    const data = await attendanceApi.getStudentAttendanceGroups({
      mp_event_name: 'T51-04_학생출결번호재발급_API',
    })

    const attendanceArr = data.map(this.adapter.gradeAttendance.from)
    return attendanceArr
  }
}

export const studentController = new StudentController()
